// Scenario override tree: schema, overridable-field allowlist, and tree-validity refinements.
import { z } from 'zod'
import { ValueWithUnitSchema } from '../base/common'
import { PumpOperationConfigSchema } from '../vertices/centrifugal-pump'
import { ValveOperationConfigSchema } from '../vertices/control-valve'
import { FlowDemandOperationConfigSchema } from '../vertices/flow-demand'
import {
  ActiveDeviceOperationConfigSchema,
  CompressorOperationConfigSchema,
} from '../base/active-device-operation'

/**
 * Shape-validates an `operation` override value: it must match ONE of the
 * known device operation unions. Per-device mode validity (a pump can't carry
 * a valve-only mode) is NOT enforced here — it is enforced at write time (the
 * device form) and by the post-resolution backstop. See the design spec §1.
 */
const OperationValueSchema = z.union([
  PumpOperationConfigSchema,
  ValveOperationConfigSchema,
  FlowDemandOperationConfigSchema,
  ActiveDeviceOperationConfigSchema,
  CompressorOperationConfigSchema,
])

/** A coupled pipe spec+size override (a size is only valid within a spec). */
const PipeSpecValueSchema = z.object({
  pipe_specification_id: z.string(),
  pipe_size_id: z.string(),
})

/**
 * A single sparse override. Discriminated on `field`; `target` is baked in per
 * variant so the value type is pinned (never `unknown`). All `field`s are
 * TOP-LEVEL keys of the target element (so applying is `clone[field] = value`),
 * except `pipe_spec`, which writes two edge keys (handled in the resolver).
 */
export const OverrideSchema = z.discriminatedUnion('field', [
  z.object({ target: z.literal('vertex'), id: z.string(), field: z.literal('operation'), value: OperationValueSchema }),
  z.object({ target: z.literal('vertex'), id: z.string(), field: z.literal('operating_diameter'), value: ValueWithUnitSchema }),
  z.object({ target: z.literal('vertex'), id: z.string(), field: z.literal('liquid_level'), value: ValueWithUnitSchema }),
  z.object({ target: z.literal('vertex'), id: z.string(), field: z.literal('surface_pressure'), value: ValueWithUnitSchema }),
  // `pressure`/`pressure_type` are pressure_boundary-only; field→type binding is enforced at write time (OVERRIDABLE_FIELDS), not by this union.
  z.object({ target: z.literal('vertex'), id: z.string(), field: z.literal('pressure'), value: ValueWithUnitSchema }),
  z.object({ target: z.literal('vertex'), id: z.string(), field: z.literal('pressure_type'), value: z.enum(['total', 'static']) }),
  z.object({ target: z.literal('edge'), id: z.string(), field: z.literal('pipe_spec'), value: PipeSpecValueSchema }),
])

export type Override = z.infer<typeof OverrideSchema>
export type OverrideField = Override['field']

export const ScenarioNodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  parent_id: z.string().nullable(),
  overrides: z.array(OverrideSchema),
})
export type ScenarioNode = z.infer<typeof ScenarioNodeSchema>

export const ScenariosSchema = z.object({
  active_id: z.string(),
  nodes: z.array(ScenarioNodeSchema).min(1),
})
  .refine((s) => new Set(s.nodes.map((n) => n.id)).size === s.nodes.length, {
    message: 'Scenario node ids must be unique',
  })
  .refine((s) => s.nodes.some((n) => n.id === s.active_id), {
    message: 'active_id must reference an existing scenario node',
  })
  .refine((s) => {
    // Every non-null parent_id must resolve to a node.
    const ids = new Set(s.nodes.map((n) => n.id))
    return s.nodes.every((n) => n.parent_id === null || ids.has(n.parent_id))
  }, { message: 'parent_id must reference an existing scenario node' })
  .refine((s) => {
    // No cycles: walking parent_id from every node must terminate at a node with parent_id === null.
    const byId = new Map(s.nodes.map((n) => [n.id, n]))
    for (const start of s.nodes) {
      const seen = new Set<string>()
      let cur: ScenarioNode | undefined = start
      while (cur && cur.parent_id !== null) {
        if (seen.has(cur.id)) return false
        seen.add(cur.id)
        cur = byId.get(cur.parent_id)
      }
    }
    return true
  }, { message: 'Scenario tree must be acyclic' })

export type Scenarios = z.infer<typeof ScenariosSchema>

/**
 * Single source of truth for which fields are overridable per element type.
 * Consumed by the schema-closure validator (closure.ts), the store's
 * routeFieldEdit (Plan 1B), the grid (Plan 1C), and pruning. Keep in sync with
 * the Override union above.
 */
export const OVERRIDABLE_FIELDS: Record<string, OverrideField[]> = {
  // vertices
  // compressor/blower/fan operation overrides are intentionally out of Phase-1 scope.
  centrifugal_pump: ['operation', 'operating_diameter'],
  control_valve: ['operation'],
  flow_demand: ['operation'],
  tank: ['liquid_level', 'surface_pressure'],
  pressure_boundary: ['pressure', 'pressure_type'],
  // edges (NOT duct)
  pipe: ['pipe_spec'],
  compressible_pipe: ['pipe_spec'],
}

/** True iff `field` may be overridden on an element of `elementType`. */
export function isFieldOverridable(elementType: string, field: OverrideField): boolean {
  return OVERRIDABLE_FIELDS[elementType]?.includes(field) ?? false
}
